<script setup lang="ts">
import { computed } from 'vue'
import xss from 'xss'
import { useI18n } from 'vue-i18n'
import { useHead } from '@unhead/vue'
import { useRouter } from 'vue-router'
import DefaultLayout from '@/layouts/default.vue'
import { useLocalizedRouter } from '@/composables/localizedRouter'
import HeaderVisualMarketplace from '@/components/HeaderVisual/HeaderVisualMarketplace.vue'
import Trustpilot from '@/components/Misc/Trustpilot.vue'
import OfferingGrid from '@/pages/marketplace/redesign/Offering/OfferingGrid.vue'
import Headline from '@/components/partials/Headline.vue'
import Hr from '@/components/partials/Hr.vue'
import Container from '@/components/Structure/Container/Container.vue'
import type { MarketplaceEstablishment } from '@/http/models/MarketplaceEstablishment'
import type CategoryResource from '@/resources/CategoryResource'
import LocationSearch from '@/components/location-search/LocationSearch.vue'
import { useSessionStore } from '@/stores/useSessionStore'
import { useBreadcrumbStore } from '@/stores/useBreadcrumbStore'
import type { Location } from "@/types/Location"

const {t, locale} = useI18n()
const {localizedRoute} = useLocalizedRouter()
const sessionStore = useSessionStore()

const props = defineProps<{
  category: CategoryResource,
  categories: CategoryResource[],
  establishments: MarketplaceEstablishment[]
}>()

const {setBreadcrumbs} = useBreadcrumbStore()

setBreadcrumbs([
  {
    route: localizedRoute({
      name: 'all-categories',
    }),
    name: `${t('terms.all')} ${t('categories.categories').toLowerCase()}`,
  },
  {
    route: localizedRoute({name: 'slug', params: {slug: props.category.slug}}),
    name: props.category.name,
  },
])

const scrollToTop = () => window.scrollTo(0, 0)

useHead({
  title: props.category.meta ? props.category.meta.title : props.category.name,
  description: props.category.meta.description,
})

const categoryLists = computed(() => {
  const numCategoriesInList = 9

  return props.categories
      .filter(c => c.attributes.slug && c.id !== props.category.id)
      .slice(0, numCategoriesInList)
})

const router = useRouter()

async function redirectToMarketplace({ location }: { query: string, location: Location }) {
  sessionStore.setLocation(location)

  await router.push(localizedRoute({name: 'marketplace'}))
}
</script>

<template>
  <DefaultLayout>
    <div class="category">
      <HeaderVisualMarketplace :image="category.header.large.url"
                               full
                               :overlay="false"
      />
      <Container>
        <div class="row">
          <div v-if="category.content"
               class="col-lg-6 category__header"
          >
            <Headline>
              <h1 v-if="category.content.title"
                  class="title"
              >
                {{ category.content.title || t('category.title') }}
              </h1>
              <i18n-t v-else
                      keypath="category.title"
                      tag="h1"
                      class="title"
                      scope="global"
              >
                <template #category>
                  {{ category.name }}
                </template>
              </i18n-t>

              <p v-if="category.content.subtitle"
                 class="line-lg"
              >
                {{ category.content.subtitle || t('category.subtitle') }}
              </p>
              <i18n-t v-else
                      keypath="category.subtitle"
                      tag="p"
                      class="line-lg"
                      scope="global"
              >
                <template #category_count>
                  <span class="branded">
                    {{ t('category.category_count') }}
                  </span>
                </template>
              </i18n-t>
            </Headline>

            <LocationSearch @submit="redirectToMarketplace"/>

            <div class="mt-2">
              <img :src="
                  locale === 'en'
                    ? '/assets/img/writtenTextEnglish.svg'
                    : '/assets/img/writtenText.svg'
                "
                   :alt="t('search.img-alt')"
                   :height="locale === 'en' ? 52 : 64"
                   :width="locale === 'en' ? 351 : 288"
                   data-not-lazy
              >
            </div>
          </div>
        </div>
        <Headline v-if="category.content">
          <i18n-t keypath="category.best_in_location"
                  tag="h2"
                  scope="global"
          >
            <template #best>
              <span class="branded">
                {{ t('category.best_rated') }}
              </span>
              <br>
            </template>
            <template #category>
              {{ category.content.restaurants }}
            </template>
          </i18n-t>
          <p>{{ category.content.restaurants_subtitle }}</p>
        </Headline>
        <OfferingGrid :items="establishments"/>
        <div v-if="category.content"
             class="mt-4 text-center"
        >
          <i18n-t tag="router-link"
                  keypath="category.check_all"
                  class="link"
                  :to="localizedRoute({ name: 'marketplace' })"
                  scope="global"
          >
            <template #restaurants>
              {{ category.content.restaurants }}
            </template>
          </i18n-t>
        </div>
        <div class="mt-20 row row--lg center-sm">
          <div v-if="category.content"
               class="col-sm-7 content"
          >
            <h2 class="mt-0">
              {{ category.content.content_title }}
            </h2>
            <Trustpilot class="my-2"
                        style="width: 290px"
            />
            <Hr/>
            <div class="text-center">
              <img class=""
                   :src="category.content.content_image_url"
                   :alt="category.content.content_image_alt"
              >
            </div>
            <!-- eslint-disable vue/no-v-html -->
            <div class="line-lg" v-html="xss(category.content.content_text)"></div>
            <!-- eslint-enable vue/no-v-html -->
          </div>
        </div>

        <div class="mt-4 row center-sm">
          <div class="col-sm-7">
            <div class="category__cta">
              <i18n-t keypath="category.search_title"
                      tag="h2"
                      class="mt-0"
                      scope="global"
              >
                <template #category>
                  {{ category.name }}
                </template>
              </i18n-t>
              <p class="mb-2">
                {{ t('category.search') }}
              </p>
              <LocationSearch @submit="redirectToMarketplace"/>
            </div>

            <h2 class="mt-15">
              {{ t('category.order_also') }}
            </h2>
            <ul class="row list__unstyled category__row">
              <li v-for="category in categoryLists"
                  :key="category.id"
                  class="col-sm-4"
              >
                <router-link :to="localizedRoute({ name: 'slug', params: { slug: category.attributes.slug || 'unknown' }})"
                             class="link"
                             @click="scrollToTop"
                >
                  {{ category.attributes.name }}
                </router-link>
              </li>
            </ul>
          </div>
        </div>
      </Container>
    </div>
  </DefaultLayout>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/breakpoints-down.scss";
@import "@/assets/css/mixins/breakpoints-up.scss";
@import "@/assets/css/mixins/styling.scss";

.container {
  margin: auto 1.5rem;

  @include sm-up {
    margin: auto auto;
  }
}

.category {
  &__header {
    position:        relative;
    display:         flex;
    justify-content: center;
    flex-direction:  column;
    margin:          -35% 0 5rem 0;

    @include lg-down {
      background: white;
      padding:    1.5rem;
      margin-top: 7rem;
      @include radius;
    }

    @include lg-up {
      margin:     5rem 0 0 0;
      min-height: 90vh;
    }
  }

  &__cta {
    background: white;
    @include radius;
    @include shadow-sm;
    padding:    2rem;
  }

  &__row {
    @include sm-up {
      margin-left:  -0.75rem;
      margin-right: -0.75rem;
    }
  }
}
</style>
